import { useLanguage } from "@hooks/useLanguage";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  InputSelect,
  InputText,
  TableData,
  TableRow,
  TableSecondary,
  Text,
} from "@pentabd/ui";

import { useMetaData } from "@hooks/inventory/settings/useMetaData";
import { SETTINGS_META_DATA } from "@constants/inventory/settings-meta-data";
import { getConvertedUnitMetaData } from "@helpers/inventory/metaDataConvert";
import { SelectController } from "@components";

const { UNIT } = SETTINGS_META_DATA;

export const SelectedProductTable = ({
  data,
  disable,
}: {
  data: any;
  disable: boolean;
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { data: unit } = useMetaData(UNIT);

  const convertedData =
    data?.map((obj: any) => {
      return {
        ...obj,
        ...obj.items,
        itemId: obj?.item?.id,
        categoryName: obj?.group?.category?.name,
        groupName: obj?.group?.name,
        brandName: obj?.brand?.name,
        ...obj.item,
      };
    }) || [];

  const {
    register,
    setValue,
    formState: { errors },
    control,
  } = useFormContext();

  return (
    <div>
      <div className="border-bottom py-10">
        <Text color="subtitle1" component="h4" weight="bold" size="lg">
          {t("PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.HEADING")}
        </Text>
      </div>

      <div className="pt-10">
        <TableSecondary
          columns={[
            {
              id: 1,
              name: t(
                "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.CATEGORY",
              ),
            },
            {
              id: 2,
              name: t(
                "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.GROUP",
              ),
            },
            {
              id: 4,
              name: t(
                "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.NAME",
              ),
            },
            {
              id: 5,
              name: t(
                "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.BRAND",
              ),
            },
            {
              id: 6,
              name: t(
                "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.IS_ASSET",
              ),
            },

            {
              id: 7,
              name: (
                <Text className="text-nowrap">
                  {t(
                    "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.UNIT_PRICE",
                  )}
                </Text>
              ),
            },

            {
              id: 8,
              name: (
                <Text className="text-nowrap">
                  {t(
                    "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.REQUESTED_AMOUNT",
                  )}
                </Text>
              ),
            },
            {
              id: 9,
              name: t(
                "PROCUREMENT_DEMAND.DEMAND_CREATE.SELECTED_PRODUCT_TABLE.COLUMN.UNIT",
              ),
            },
          ]}
        >
          {convertedData.map(
            (
              {
                quantity,
                unitPrice,
                nameBn,
                nameEn,
                categoryName,
                groupName,
                brandName,
                id,
                isAsset,
                group,
                itemUnit,
              }: any,
              index: number,
            ) => {
              setValue(`items.${index}.id`, id);
              setValue(`items.${index}.isAsset`, isAsset);
              return (
                <TableRow key={index}>
                  <TableData>
                    <Text color="subtitle1">{categoryName}</Text>
                  </TableData>

                  <TableData>
                    <Text color="subtitle1">{groupName}</Text>
                  </TableData>

                  <TableData>
                    <Text color="subtitle1">
                      {language === "bn" ? nameBn : nameEn}
                    </Text>
                  </TableData>

                  <TableData>
                    <Text color="subtitle1">{brandName}</Text>
                  </TableData>

                  <TableData>
                    <Text color="subtitle1">{isAsset ? "Asset" : "Item"}</Text>
                  </TableData>

                  <TableData>
                    <InputText
                      {...register(`items.${index}.approximateUnitPrice`, {
                        valueAsNumber: true,
                        required: "Unit Price is required",
                      })}
                      placeholder="Unit price"
                      error={errors as any}
                      type="number"
                      controlling
                      disabled={disable}
                    />
                  </TableData>
                  <TableData>
                    <InputText
                      {...register(`items.${index}.quantity`, {
                        valueAsNumber: true,
                        required: "Requested amount is required",
                      })}
                      placeholder="Requested Amount"
                      error={errors as any}
                      type="number"
                      controlling
                      disabled={disable}
                    />
                  </TableData>
                  <TableData>
                    <SelectController
                      register={{
                        ...register(`items.${index}.itemUnit`, {
                          required: "unit is required",
                        }),
                      }}
                      error={errors as any}
                      name={`items.${index}.itemUnit`}
                      options={getConvertedUnitMetaData(unit, language)}
                      control={control}
                      setValue={setValue}
                      disabled={disable}
                      portal
                    />
                  </TableData>
                </TableRow>
              );
            },
          )}
        </TableSecondary>
      </div>
    </div>
  );
};
